// State management + desktop-browser detection for the dev mobile viewport
// preview tool. Plan: docs/planning/dev_mobile_viewport_preview.md §5, §6, §9.
//
// Storage contract:
//   - localStorage key `bb-dev-viewport` stores a JSON blob `{ preset, orientation }`.
//   - When the key is absent (or malformed), the simulator is OFF.
//   - `useDevMode.disableDevMode` removes the key directly (Q3 default); this
//     hook reacts to the resulting `storage` event so state stays in sync
//     across tabs and across dev-mode toggles.
//
// Public surface intentionally exposes a `state` object so consumers don't
// have to deal with `null` preset vs. "off" — the `active` boolean + resolved
// `size` cover the common cases.

import { useCallback, useEffect, useMemo, useState } from "react";
import {
  isDevViewportOrientation,
  isDevViewportPresetId,
  resolveDevViewportSize,
  type DevViewportOrientation,
  type DevViewportPresetId,
} from "@/components/dev/viewport/devViewportPresets";

export const DEV_VIEWPORT_STORAGE_KEY = "bb-dev-viewport";

/**
 * Same-tab broadcast channel. The browser's native `storage` event only fires
 * in OTHER tabs/windows, so when the impersonation popover writes a preset
 * the `DevViewportFrame` (which also calls this hook from a different point
 * in the tree) would never re-read. We dispatch a CustomEvent on the same
 * window so all hook instances stay in sync within a single tab. Dev sync
 * note c238ce65 (Phase 2 of the dev mobile viewport feature).
 */
const DEV_VIEWPORT_CHANGE_EVENT = "bb-dev-viewport-change";

const DEFAULT_ORIENTATION: DevViewportOrientation = "portrait";
const DESKTOP_MIN_WIDTH_PX = 900;

type StoredShape = {
  preset: DevViewportPresetId;
  orientation: DevViewportOrientation;
};

export type DevViewportState = {
  preset: DevViewportPresetId | null;
  orientation: DevViewportOrientation;
  active: boolean;
  size: { width: number; height: number } | null;
};

export type UseDevViewportResult = {
  state: DevViewportState;
  setPreset: (id: DevViewportPresetId | null) => void;
  setOrientation: (orientation: DevViewportOrientation) => void;
  isDesktopBrowser: boolean;
};

function readStoredViewport(): StoredShape | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(DEV_VIEWPORT_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as unknown;
    if (
      parsed &&
      typeof parsed === "object" &&
      isDevViewportPresetId((parsed as StoredShape).preset) &&
      isDevViewportOrientation((parsed as StoredShape).orientation)
    ) {
      return parsed as StoredShape;
    }
  } catch {
    /* corrupt entry — treat as no preset */
  }
  return null;
}

function writeStoredViewport(value: StoredShape | null): void {
  if (typeof window === "undefined") return;
  try {
    if (value === null) {
      window.localStorage.removeItem(DEV_VIEWPORT_STORAGE_KEY);
    } else {
      window.localStorage.setItem(DEV_VIEWPORT_STORAGE_KEY, JSON.stringify(value));
    }
    window.dispatchEvent(new CustomEvent(DEV_VIEWPORT_CHANGE_EVENT));
  } catch {
    /* private mode / quota — silently ignore */
  }
}

/**
 * Conservative "is this a desktop browser?" heuristic per plan §5. We err on
 * the side of "yes" — the worst case is a tablet/dev sees a toggle they don't
 * need, which is fine.
 */
function detectDesktopBrowser(): boolean {
  if (typeof window === "undefined") return false;
  const widthOk = window.innerWidth >= DESKTOP_MIN_WIDTH_PX;
  const pointerOk = (() => {
    if (typeof window.matchMedia !== "function") return true;
    try {
      return window.matchMedia("(pointer: fine) and (hover: hover)").matches;
    } catch {
      return true;
    }
  })();
  const uaChMobile = (() => {
    const data = (navigator as Navigator & { userAgentData?: { mobile?: boolean } })
      .userAgentData;
    return data?.mobile === true;
  })();
  return widthOk && pointerOk && !uaChMobile;
}

export function useDevViewport(): UseDevViewportResult {
  const [stored, setStored] = useState<StoredShape | null>(() => readStoredViewport());
  const [isDesktopBrowser, setIsDesktopBrowser] = useState<boolean>(() => detectDesktopBrowser());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onStorage = (e: StorageEvent) => {
      if (e.key !== null && e.key !== DEV_VIEWPORT_STORAGE_KEY) return;
      setStored(readStoredViewport());
    };
    const onSameTabChange = () => setStored(readStoredViewport());
    window.addEventListener("storage", onStorage);
    window.addEventListener(DEV_VIEWPORT_CHANGE_EVENT, onSameTabChange);
    return () => {
      window.removeEventListener("storage", onStorage);
      window.removeEventListener(DEV_VIEWPORT_CHANGE_EVENT, onSameTabChange);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setIsDesktopBrowser(detectDesktopBrowser());
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const setPreset = useCallback((id: DevViewportPresetId | null) => {
    if (id === null) {
      writeStoredViewport(null);
      setStored(null);
      return;
    }
    setStored((prev) => {
      const next: StoredShape = { preset: id, orientation: prev?.orientation ?? DEFAULT_ORIENTATION };
      writeStoredViewport(next);
      return next;
    });
  }, []);

  const setOrientation = useCallback((orientation: DevViewportOrientation) => {
    setStored((prev) => {
      if (!prev) return prev;
      const next: StoredShape = { ...prev, orientation };
      writeStoredViewport(next);
      return next;
    });
  }, []);

  const state = useMemo<DevViewportState>(() => {
    if (!stored) {
      return { preset: null, orientation: DEFAULT_ORIENTATION, active: false, size: null };
    }
    return {
      preset: stored.preset,
      orientation: stored.orientation,
      active: true,
      size: resolveDevViewportSize(stored.preset, stored.orientation),
    };
  }, [stored]);

  return { state, setPreset, setOrientation, isDesktopBrowser };
}

/**
 * Snapshot helper for non-React consumers (currently only used by tests and
 * by `BugReportButton`'s metadata composition path, which lives inside React
 * but uses this as a cheaper read than mounting the full hook). Returns
 * `null` when the simulator is off.
 */
export function readDevViewportSnapshot(): {
  preset: DevViewportPresetId;
  orientation: DevViewportOrientation;
  width: number;
  height: number;
} | null {
  const stored = readStoredViewport();
  if (!stored) return null;
  const size = resolveDevViewportSize(stored.preset, stored.orientation);
  return { preset: stored.preset, orientation: stored.orientation, ...size };
}

/**
 * Canonical "wipe the viewport simulator" helper for non-React callers
 * (currently `useDevMode.disableDevMode` and its on-mount clear). Goes through
 * `writeStoredViewport` so the same-tab CustomEvent fires and every
 * `useDevViewport` instance picks up the change.
 */
export function clearDevViewportState(): void {
  writeStoredViewport(null);
}
